import React, { useState, useEffect } from 'react';
import UchroniaOS from './UchroniaOS';
import { LoginScreen } from './components/os/LoginScreen';
import { ShutdownScreen } from './components/os/ShutdownScreen';
import { KernelCrashAlert } from './components/os/KernelCrashAlert';
import { LanguageSelector } from './components/os/LanguageSelector';
import { TutorialRecovery } from './components/TutorialRecovery';
import { PowerButton } from './components/PowerButton';
import { useTutorial } from './hooks/useTutorial';
import { forceQuitApplication, setFullscreen } from './utils/electron';
import { isWeb } from './utils/platform';
import i18n from './i18n/config';
import './App.css';

function App() {
  // États pour gérer les différentes phases
  const [isPoweredOn, setIsPoweredOn] = useState<boolean>(false);
  const [bootComplete, setBootComplete] = useState<boolean>(false);
  const [languageSelected, setLanguageSelected] = useState<boolean>(() => {
    // Vérifier si la langue a déjà été sélectionnée
    return localStorage.getItem('ALBION_LANGUAGE') !== null;
  });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isShuttingDown, setIsShuttingDown] = useState(false);
  const [isKernelCrashing, setIsKernelCrashing] = useState(false);
  const [isKernelCrash, setIsKernelCrash] = useState(false);
  const [isRestart, setIsRestart] = useState(false);

  // Hook du tutoriel pour le système de récupération
  const { step, sendRecoveryHint } = useTutorial();

  const handlePowerOn = () => {
    setIsPoweredOn(true);
    
    // Activer le plein écran quand on démarre le système
    if (isWeb()) {
      setTimeout(() => {
        const savedSettings = localStorage.getItem('ALBION_GAME_SETTINGS');
        let shouldApplyFullscreen = true;
        
        if (savedSettings) {
          try {
            const settings = JSON.parse(savedSettings);
            shouldApplyFullscreen = settings.fullscreenMode !== false;
          } catch (e) {
            console.error('Error parsing game settings:', e);
          }
        }
        
        console.log('App - Power on: applying fullscreen:', shouldApplyFullscreen);
        setFullscreen(shouldApplyFullscreen);
      }, 100);
    }
  };

  const handleLanguageSelected = (language: 'fr' | 'en') => {
    // Sauvegarder la langue sélectionnée
    localStorage.setItem('ALBION_LANGUAGE', language);
    
    // Changer immédiatement la langue dans i18n
    i18n.changeLanguage(language);
    
    // Émettre l'événement de changement de langue
    window.dispatchEvent(new CustomEvent('LANGUAGE_CHANGED', { detail: { language } }));
    
    // Marquer la langue comme sélectionnée
    setLanguageSelected(true);
  };

  const handleLoginScreenReady = () => {
    // Appelé quand le boot de LoginScreen est terminé
    setBootComplete(true);
  };

  // Gestion globale du plein écran pour tout le jeu
  useEffect(() => {
    // Délai pour éviter les conflits au démarrage
    const timeout = setTimeout(() => {
      // Si on est sur l'écran de démarrage (version web), pas de plein écran
      if (isWeb() && !isPoweredOn) {
        console.log('App - Power button screen: disabling fullscreen');
        setFullscreen(false);
        document.body.classList.remove('fullscreen-mode');
        return;
      }

      // Appliquer le plein écran au démarrage si activé dans les paramètres
      const savedSettings = localStorage.getItem('ALBION_GAME_SETTINGS');
      let shouldApplyFullscreen = true; // Par défaut : plein écran activé
      
      if (savedSettings) {
        try {
          const settings = JSON.parse(savedSettings);
          shouldApplyFullscreen = settings.fullscreenMode !== false; // Tout sauf false explicite
          console.log('App - Parsed settings fullscreenMode:', settings.fullscreenMode);
        } catch (e) {
          console.warn('App - Error parsing settings:', e);
        }
      }
      
      // Initialiser la classe CSS selon l'état du plein écran
      if (shouldApplyFullscreen) {
        document.body.classList.add('fullscreen-mode');
      } else {
        document.body.classList.remove('fullscreen-mode');
      }
      
      console.log('App - Applying fullscreen on startup:', shouldApplyFullscreen);
      setFullscreen(shouldApplyFullscreen);
    }, 500); // Délai de 500ms pour laisser l'interface se charger

    // Écouter les changements de paramètres depuis n'importe quel composant
    const handleSettingsChange = (e: CustomEvent) => {
      const newSettings = e.detail;
      const fullscreenValue = newSettings.fullscreenMode !== false; // Défaut à true si undefined
      console.log('App - Settings changed globally, applying fullscreen:', fullscreenValue, 'from:', newSettings.fullscreenMode);
      
      // Appliquer immédiatement le changement
      setFullscreen(fullscreenValue);
    };

    window.addEventListener('GAME_SETTINGS_CHANGED', handleSettingsChange as EventListener);
    
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('GAME_SETTINGS_CHANGED', handleSettingsChange as EventListener);
    };
  }, [isPoweredOn]); // Ajouter isPoweredOn pour réagir aux changements d'état

  // Écouter les demandes d'arrêt système, crash kernel et la touche Échap
  useEffect(() => {
    const handleShutdownRequest = (e: Event) => {
      const customEvent = e as CustomEvent;
      const isKernel = customEvent.detail?.isKernelCrash || false;
      const isRestartRequest = customEvent.detail?.isRestart || false;
      console.log('App - SYSTEM_SHUTDOWN_REQUESTED received, isKernelCrash:', isKernel, 'isRestart:', isRestartRequest, 'detail:', customEvent.detail);
      setIsKernelCrash(isKernel);
      setIsRestart(isRestartRequest);
      setIsShuttingDown(true);
    };

    const handleKernelCrash = () => {
      setIsKernelCrashing(true);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Échap pour sortir du plein écran en cas de problème
      if (e.key === 'Escape') {
        console.log('App - Escape pressed, exiting fullscreen');
        setFullscreen(false);
      }
    };

    window.addEventListener('SYSTEM_SHUTDOWN_REQUESTED', handleShutdownRequest);
    window.addEventListener('KERNEL_CRASH', handleKernelCrash);
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('SYSTEM_SHUTDOWN_REQUESTED', handleShutdownRequest);
      window.removeEventListener('KERNEL_CRASH', handleKernelCrash);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleShutdownComplete = () => {
    console.log('App - handleShutdownComplete called, isKernelCrash:', isKernelCrash, 'isRestart:', isRestart);
    
    if (isWeb()) {
      // Pour la version web, retourner à l'écran de démarrage au lieu de recharger
      console.log('App - Web version: returning to power button screen');
      setIsShuttingDown(false);
      setIsPoweredOn(false);
      setBootComplete(false);
      setIsLoggedIn(false);
      setIsKernelCrash(false);
      setIsRestart(false);
    } else {
      // Pour Electron, comportement original
      if (isKernelCrash || isRestart) {
        // Kernel crash ou redémarrage : recharger la page
        console.log('App - Kernel crash or restart detected, reloading page');
        setIsShuttingDown(false);
        setIsKernelCrash(false);
        setIsRestart(false);
        window.location.reload();
      } else {
        // Shutdown normal : fermer l'application
        console.log('App - Normal shutdown, quitting application');
        forceQuitApplication();
      }
    }
  };

  const handleKernelCrashComplete = () => {
    // Fermer l'alerte, le shutdown prend le relais
    setIsKernelCrashing(false);
  };

  return (
    <div className="App">
      {/* Étape 0 : Écran de démarrage avec bouton power (version web uniquement) */}
      {!isPoweredOn && isWeb() && (
        <PowerButton onPowerOn={handlePowerOn} />
      )}
      
      {/* Séquence normale : Boot → Sélection langue → Login → OS */}
      {(isPoweredOn || !isWeb()) && (
        <>
          {/* Étape 1 : Boot (LoginScreen gère l'animation de boot) */}
          {!bootComplete && !isLoggedIn && (
            <LoginScreen 
              onLogin={() => setIsLoggedIn(true)} 
              onBootComplete={handleLoginScreenReady}
              showSignIn={false}
            />
          )}
          
          {/* Étape 2 : Sélection de langue (après boot, avant signin) */}
          {bootComplete && !languageSelected && !isLoggedIn && (
            <LanguageSelector onLanguageSelected={handleLanguageSelected} />
          )}
          
          {/* Étape 3 : Écran de connexion (après sélection langue) */}
          {bootComplete && languageSelected && !isLoggedIn && (
            <LoginScreen 
              onLogin={() => setIsLoggedIn(true)} 
              onBootComplete={handleLoginScreenReady}
              showSignIn={true}
            />
          )}
          
          {/* Étape 4 : OS (après login) */}
          {isLoggedIn && (
            <>
              <UchroniaOS />
              
              {/* Système de récupération du tutoriel */}
              {step !== 'FINISH' && (
                <TutorialRecovery 
                  currentStep={step}
                />
              )}
            </>
          )}
        </>
      )}
      
      {/* Écran d'extinction par-dessus */}
      <ShutdownScreen 
        isVisible={isShuttingDown} 
        onComplete={handleShutdownComplete}
        isKernelCrash={isKernelCrash}
        isRestart={isRestart}
      />

      {/* Alerte de crash kernel par-dessus tout */}
      <KernelCrashAlert 
        isVisible={isKernelCrashing} 
        onComplete={handleKernelCrashComplete}
      />
    </div>
  );
}

export default App;